import { deleteWaMapping } from '../../../redux/reducers/smartSendWhatsAppSlice';
// READ-ONLY import. `smartSendSlice` is byte-identical (contract §1) — `deleteMapping` is the
// EXISTING e-mail-side thunk (`smartSendSlice.ts:227` → `DELETE DataSourcesSender/DeleteMapping/
// {id}`), already used by the manage tab. No new route, no edit to that file.
import { deleteMapping } from '../../../redux/reducers/smartSendSlice';
import { eSendChannel } from '../../../Models/DataSources/SmartSend';

// SmartSend part B — removing the WhatsApp from a WHATSAPP-ONLY campaign (contract §6, v1.23c/d).
//
// 🔴 WHY THIS IS A MODULE AND NOT TWO COPIES OF FOUR LINES. Two screens raise the removal (the
// picker's un-tick + "המשך", and the mapping tab's "הסר וואטסאפ"), and the sequence below has a
// MANDATORY ORDER whose reason is invisible at the call site. A rule copied into two components
// drifts — that is the lesson the contract records five times over — and the drift here leaves
// rows in the database that no screen can reach.

export type WaRemovalChoice = 'switchToEmail' | 'deleteAll';

export interface WaRemovalResult {
    ok: boolean;
    /** Which call failed. `null` on success. */
    stage: 'leg' | 'mapping' | null;
    /** The §4 `Message` of a failed LEG delete. Always null for a failed mapping delete — that
     *  route answers in the e-mail side's own vocabulary, not §4's. */
    message: string | null;
}

const ok = (p: any): boolean => !!p && (p.StatusCode === 200 || p.StatusCode === 201);

export const runWaRemoval = async (
    dispatch: any, campaignId: number, choice: WaRemovalChoice,
): Promise<WaRemovalResult> => {
    // ── (1) THE LEG, ALWAYS FIRST, AND THE ORDER IS NOT A PREFERENCE ────────────────────────
    // `CampaignsToDataSources_Delete` is a live SP that predates `CampaignWhatsAppLeg` and knows
    // nothing about it (§6). Delete the mapping first and a failure in between leaves an ORPHAN
    // LEG carrying `SuppressEmail = 1` and pointing at a campaign with no source — and
    // `GetPendingLegs` joins `Campaigns`, not the mapping, so it would list that campaign in
    // "ממתינים לוואטסאפ" forever with nothing on any screen able to clear it.
    //
    // 🔴 `confirmSuppressedDelete: true` — THIS IS THE PARAMETER THE DIALOG EXISTS TO FILL.
    // Deleting this leg removes the only thing suppressing the e-mail, so the route answers
    // `409 WA_SUPPRESS_CONFIRM_REQUIRED` without it. Both branches send it: (א) makes the mail
    // the campaign's channel, (ב) deletes the campaign's mapping outright — in each case a human
    // chose, which is exactly what the flag asserts.
    const legRes: any = await dispatch(deleteWaMapping({ campaignId, confirmSuppressedDelete: true }));
    const legPayload = legRes && legRes.payload ? legRes.payload : {};
    if (!ok(legPayload)) {
        const code: string = legPayload.Message ?? 'WA_INTERNAL';
        // ⚠️ RECEIVING THE CONFIRM CODE HERE IS OUR BUG, NOT A STATE TO SHOW THE OPERATOR.
        // We just sent the flag, so a `409 WA_SUPPRESS_CONFIRM_REQUIRED` can only mean the
        // request went out without it. Rendering its text ("אישור ההסרה יחזיר את שליחת המייל")
        // to someone who has just confirmed exactly that would read as the screen not having
        // heard them. `WA_INTERNAL` is the honest answer for a fault on our side.
        const shown = code === 'WA_SUPPRESS_CONFIRM_REQUIRED' ? 'WA_INTERNAL' : code;
        // 🔴 C-20 (v1.23g) — AND THIS `return` IS A STOP CONDITION, NOT AN EARLY EXIT.
        // A refused leg delete is a FINAL refusal: the route says no when the WhatsApp has
        // already been sent (`WA_ALREADY_SENT`) or when the balance was charged and not
        // refunded, and neither of those becomes true again later.
        // Continuing to step 2 anyway would be the worst outcome available here — worse than
        // doing nothing at all: the leg SURVIVES carrying `SuppressEmail = 1` while the
        // campaign-to-source link is deleted underneath it, and with the mapping gone there is
        // no screen in the product that can reach that leg again. An unreachable row that
        // suppresses a mail is not a state anyone can get out of.
        // ⇒ It applies to BOTH branches: (א) ends here too, because (א) IS step 1.
        return { ok: false, stage: 'leg', message: shown };
    }

    // (א) — the leg is gone, `SuppressEmail` with it, and the campaign is an ordinary e-mail
    // campaign whose mapping is untouched. Nothing further to do.
    if (choice === 'switchToEmail') return { ok: true, stage: null, message: null };

    // ── (2) THE MAPPING ─────────────────────────────────────────────────────────────────────
    // 🟡 THE TWO CALLS ARE NOT ATOMIC, and that is a KNOWN AND ACCEPTED gap for the first round
    // (§6). What is NOT acceptable is swallowing a failure between them, so this reports the
    // stage — and there is deliberately NO automatic retry here: re-running the sequence would
    // re-issue a leg delete that has already succeeded, and inventing recovery for a gap the
    // contract chose to accept is how an accepted gap turns into an unaccountable one.
    // 🔴 EMAIL, AND THAT IS DELIBERATE — see the comment at :58-61: what this step removes is
    // "the campaign-to-source link", i.e. the E-MAIL channel's mapping row, which is the only
    // mapping row the product can create today (measured 21.09: all 18 rows are channel 1).
    // Naming the channel keeps today's behaviour byte-identical.
    // ⚠️ DEBT: once a channel-3 mapping row can exist, "remove WhatsApp" must delete CHANNEL 3's
    //    row and leave the e-mail one alone. That lands with the container flow (79 §5.3 step 5).
    const mapRes: any = await dispatch(deleteMapping({ campaignId, channel: eSendChannel.EMAIL }));
    if (!ok(mapRes && mapRes.payload)) return { ok: false, stage: 'mapping', message: null };

    return { ok: true, stage: null, message: null };
};

export default runWaRemoval;
